import React, { useState } from 'react';
import type { Person, FamilyTreeData } from './types';
import PersonCard from './PersonCard';
import { Plus, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

interface FamilyTreeViewProps {
  data: FamilyTreeData;
  onPersonSelect?: (person: Person) => void;
  onPersonEdit?: (person: Person) => void;
  onAddPerson?: () => void;
  selectedPersonId?: string;
}

const FamilyTreeView: React.FC<FamilyTreeViewProps> = ({
  data,
  onPersonSelect,
  onPersonEdit,
  onAddPerson,
  selectedPersonId
}) => {
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Sample data for demonstration
  const samplePeople: Record<string, Person> = {
    'person-1': {
      id: 'person-1',
      name: 'Te Aroha Smith',
      birthDate: '1920-03-15',
      deathDate: '1995-12-08',
      birthPlace: 'Rotorua',
      gender: 'female',
      iwi: ['Ngāti Tūwharetoa'],
      hapu: ['Ngāti Te Rangiita'],
      children: ['person-2', 'person-3'],
      notes: 'Kaumātua and respected elder'
    },
    'person-2': {
      id: 'person-2',
      name: 'Wiremu Smith',
      birthDate: '1945-07-22',
      birthPlace: 'Auckland',
      gender: 'male',
      iwi: ['Ngāti Tūwharetoa'],
      parents: ['person-1'],
      children: ['person-4', 'person-5'],
      spouses: ['person-6']
    },
    'person-3': {
      id: 'person-3',
      name: 'Mere Jones',
      birthDate: '1948-11-03',
      birthPlace: 'Wellington',
      gender: 'female',
      iwi: ['Ngāti Tūwharetoa'],
      parents: ['person-1'],
      children: ['person-7']
    },
    'person-4': {
      id: 'person-4',
      name: 'Tane Smith-Wilson',
      birthDate: '1975-04-18',
      birthPlace: 'Hamilton',
      gender: 'male',
      iwi: ['Ngāti Tūwharetoa', 'Te Arawa'],
      parents: ['person-2', 'person-6'],
      children: ['person-8']
    },
    'person-5': {
      id: 'person-5',
      name: 'Kiri Smith-Wilson',
      birthDate: '1978-09-12',
      birthPlace: 'Hamilton',
      gender: 'female',
      iwi: ['Ngāti Tūwharetoa', 'Te Arawa'],
      parents: ['person-2', 'person-6']
    },
    'person-6': {
      id: 'person-6',
      name: 'Maria Wilson',
      birthDate: '1950-02-28',
      birthPlace: 'Rotorua',
      gender: 'female',
      iwi: ['Te Arawa'],
      hapu: ['Ngāti Pikiao'],
      children: ['person-4', 'person-5'],
      spouses: ['person-2']
    },
    'person-7': {
      id: 'person-7',
      name: 'Hemi Jones-Brown',
      birthDate: '1980-06-14',
      birthPlace: 'Christchurch',
      gender: 'male',
      iwi: ['Ngāti Tūwharetoa'],
      parents: ['person-3']
    },
    'person-8': {
      id: 'person-8',
      name: 'Aroha Smith-Wilson',
      birthDate: '2005-01-20',
      birthPlace: 'Auckland',
      gender: 'female',
      iwi: ['Ngāti Tūwharetoa', 'Te Arawa'],
      parents: ['person-4']
    }
  };

  const currentData = Object.keys(data.people).length > 0 ? data : { 
    people: samplePeople, 
    rootPersonId: 'person-1' 
  };

  // Calculate tree layout
  const calculateLayout = () => {
    const people = currentData.people;
    const layout: Record<string, { x: number; y: number; level: number }> = {};
    
    // Simple layout algorithm - arrange by generations
    const visited = new Set<string>();
    const levels: string[][] = [];
    
    // Find root person (person with no parents or specified root)
    const rootId = currentData.rootPersonId || Object.keys(people).find(id => 
      !people[id].parents || people[id].parents.length === 0
    ) || Object.keys(people)[0];
    
    // BFS to organize by levels
    const queue: Array<{ id: string; level: number }> = [{ id: rootId, level: 0 }];
    
    while (queue.length > 0) {
      const { id, level } = queue.shift()!;
      
      if (visited.has(id)) continue;
      visited.add(id);
      
      if (!levels[level]) levels[level] = [];
      levels[level].push(id);
      
      // Add children to next level
      const person = people[id];
      if (person.children) {
        person.children.forEach(childId => {
          if (!visited.has(childId)) {
            queue.push({ id: childId, level: level + 1 });
          }
        });
      }
    }
    
    // Position people
    levels.forEach((levelPeople, level) => {
      const levelWidth = levelPeople.length * 250;
      const startX = -levelWidth / 2;
      
      levelPeople.forEach((personId, index) => {
        layout[personId] = {
          x: startX + (index * 250) + 125,
          y: level * 200,
          level
        };
      });
    });
    
    return layout;
  };

  const layout = calculateLayout();

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPanX(e.clientX - dragStart.x);
      setPanY(e.clientY - dragStart.y);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 2));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.5));
  const handleReset = () => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
  };

  return (
    <div className="relative w-full h-[600px] bg-gray-50 rounded-lg overflow-hidden border">
      {/* Controls */}
      <div className="absolute top-4 right-4 z-10 flex space-x-2">
        <button
          onClick={handleZoomIn}
          className="p-2 bg-white rounded-lg shadow hover:bg-gray-50"
          title="Zoom In"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
        <button
          onClick={handleZoomOut}
          className="p-2 bg-white rounded-lg shadow hover:bg-gray-50"
          title="Zoom Out"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
        <button
          onClick={handleReset}
          className="p-2 bg-white rounded-lg shadow hover:bg-gray-50"
          title="Reset View"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
        {onAddPerson && (
          <button
            onClick={onAddPerson}
            className="p-2 bg-ocean-teal text-white rounded-lg shadow hover:bg-ocean-deep"
            title="Add Person"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Tree Canvas */}
      <div
        className="w-full h-full cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          className="relative"
          style={{
            transform: `translate(${panX + 400}px, ${panY + 100}px) scale(${zoom})`,
            transformOrigin: '0 0'
          }}
        >
          {/* Connection Lines */}
          <svg 
            className="absolute inset-0 pointer-events-none"
            style={{ width: '100%', height: '100%', overflow: 'visible' }}
          >
            {Object.entries(currentData.people).map(([personId, person]) => {
              const personLayout = layout[personId];
              if (!personLayout || !person.children) return null;

              return person.children.map(childId => {
                const childLayout = layout[childId];
                if (!childLayout) return null;

                return (
                  <line
                    key={`${personId}-${childId}`}
                    x1={personLayout.x + 100}
                    y1={personLayout.y + 80}
                    x2={childLayout.x + 100}
                    y2={childLayout.y}
                    stroke="#9CA3AF"
                    strokeWidth="2"
                  />
                );
              });
            })}
          </svg>

          {/* Person Cards */}
          {Object.entries(currentData.people).map(([personId, person]) => {
            const personLayout = layout[personId];
            if (!personLayout) return null;

            return (
              <div
                key={personId}
                className="absolute"
                style={{
                  left: personLayout.x,
                  top: personLayout.y,
                  transform: 'translate(-50%, 0)'
                }}
              >
                <PersonCard
                  person={person}
                  onViewDetails={onPersonSelect}
                  onEdit={onPersonEdit}
                  isSelected={selectedPersonId === personId}
                  compact={true}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FamilyTreeView;
